import ClearIcon from '@mui/icons-material/Close'
import { FC, useState } from 'react'
import {
  Autocomplete,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  FormHelperText,
  IconButton,
  TextField
} from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { AccountOption } from '../../interfaceTypes/common'

interface SelectControllerProps {
  name: string
  label: string
  required?: boolean
  disabled?: boolean
  options: AccountOption[]
  customOnChange?: (option: AccountOption | null) => void
  value?: AccountOption
  /**
   * Show an × to unset the field. Opt-in: a Select with no empty option cannot otherwise be undone
   * once picked, but plenty of callers want exactly that, so nothing changes unless asked.
   */
  clearable?: boolean
  /**
   * Render as a type-ahead instead of a plain Select, matching the Bank Account / GL Account
   * dropdowns: typing filters the list, the closest match stays highlighted, and Tab or Enter
   * commits it without reaching for the mouse. Opt-in — the stored form value is the option's
   * `value` either way, so switching a caller over changes nothing for submit or validation.
   */
  searchable?: boolean
}
const SelectController: FC<SelectControllerProps> = ({
  name,
  label,
  options,
  required,
  disabled,
  customOnChange,
  clearable,
  searchable
}) => {
  const { control } = useFormContext()

  // Whether the user has typed a filter into the searchable variant. This gates auto-selection:
  // MUI's `autoSelect` commits the highlighted option on *any* blur, so leaving it always on means
  // clicking the field and clicking away — without typing a thing — silently writes the first
  // option. On a payables form that quietly assigns a payment method nobody chose.
  const [hasTypedFilter, setHasTypedFilter] = useState(false)

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) =>
        searchable ? (
          <Autocomplete
            options={options}
            // The form holds the bare value (an enum member, say), so map it back to its option
            // for display. An unknown or empty value shows as a blank input rather than throwing.
            value={options.find(option => option.value === field.value) ?? null}
            disabled={disabled}
            onChange={(_, option) => {
              field.onChange(option?.value ?? '')
              customOnChange?.(option)
            }}
            onBlur={() => {
              setHasTypedFilter(false)
              field.onBlur()
            }}
            // `reason` separates a real keystroke from MUI echoing the label back after a
            // selection ('reset') or a clear ('clear'), which must not count as typing.
            onInputChange={(_, typed, reason) => setHasTypedFilter(reason === 'input' && !!typed.trim())}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, selected) => option.value === selected.value}
            // The pair behind "type, then Tab": autoHighlight keeps the closest match highlighted
            // as the input narrows, autoSelect commits that highlight on Tab/blur. Both are armed
            // only while a typed filter is standing, so opening the list and leaving changes nothing.
            autoHighlight={hasTypedFilter}
            autoSelect={hasTypedFilter}
            // Without an explicit clear there is no way back to empty, same as the Select above.
            disableClearable={!clearable}
            renderInput={params => (
              <TextField
                {...params}
                label={label}
                required={required}
                error={!!error}
                helperText={error?.message}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.8125rem'
                  }
                }}
              />
            )}
          />
        ) : (
          <FormControl fullWidth disabled={disabled} error={!!error}>
            <InputLabel id={`${name}-select-label`}>{label}</InputLabel>
            <Select
              {...field}
              label={label}
              labelId={`${name}-select-label`}
              required={required}
              value={field.value ?? ''}
              onChange={e => {
                field.onChange(e.target.value)
                const selectedOption = options.find(option => option.value === e.target.value) || null
                customOnChange?.(selectedOption)
              }}
              endAdornment={
                clearable && field.value && !disabled ? (
                  <IconButton
                    size="small"
                    aria-label={`Clear ${label}`}
                    // Sits left of the dropdown arrow, which owns the right edge of the control.
                    sx={{ mr: 2.5 }}
                    // Without this the Select opens its menu before the clear ever runs.
                    onMouseDown={event => event.stopPropagation()}
                    onClick={() => {
                      field.onChange('')
                      customOnChange?.(null)
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null
              }
            >
              {options.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )
      }
    />
  )
}

export default SelectController
